import { type FC, useContext, useEffect } from 'react'
import styled, { css } from 'styled-components'

import type { IFooter, IPageConfig, IPageConfigFields } from 'contentful-shared'
import { BreadcrumbContext } from 'bl-common/src/context/BreadcrumbContext'
import { Footer } from 'bl-common/src/units/Footer/Footer'

import { Navigation } from './Navigation'
import { SiteConfig } from './SiteConfig'

interface PageBaseProps {
  children: React.ReactNode
  config?: IPageConfig
  breadcrumb?: string
  scrollHeader?: boolean
  isFramed?: boolean
  footer?: IFooter
  hideFooter?: boolean
  hasSubnavigation?: boolean
  configOverride?: Partial<IPageConfigFields>
}

const Main = styled.main<{ $withNavPadding: boolean }>`
  ${({ theme, $withNavPadding }) =>
    $withNavPadding &&
    css`
      padding-top: ${theme.spacing[6]};
    `}
`

const Page: FC<PageBaseProps> = ({
  config,
  configOverride = {},
  breadcrumb,
  footer,
  hideFooter,
  hasSubnavigation,
  children,
}) => {
  const { saveBreadcrumb } = useContext(BreadcrumbContext)

  const pageConfig = { ...config?.fields, ...configOverride }
  const {
    hasFooter = !hideFooter,
    hasAbsolutelyPositionedNavigation,
    hasWhiteNavigation,
    hideNavigation,
    isDarkMode,
  } = pageConfig

  // Navigation position options has changed to be either 'absolute' or 'fixed'. Previously, it could also be 'static'.
  // We still need to keep the 'hasAbsolutelyPositionedNavigation' setting in contentful to be able to control
  // if pages should be under the navigation or with padding top offset from the navigation.
  const withNavPadding = !hasAbsolutelyPositionedNavigation && !hideNavigation

  useEffect(() => {
    if (breadcrumb && saveBreadcrumb) {
      saveBreadcrumb(breadcrumb)
    }
  }, [])

  return (
    <>
      <Navigation
        isTransparent={!!hasAbsolutelyPositionedNavigation}
        isWhiteColored={!!hasWhiteNavigation || isDarkMode}
        hasSubnavigation={hasSubnavigation}
        hideNavigation={hideNavigation}
      />
      <Main $withNavPadding={withNavPadding}>{children}</Main>
      {hasFooter && (
        <SiteConfig>
          {({ footer: configFooter }) => (
            <Footer footer={footer || configFooter} />
          )}
        </SiteConfig>
      )}
    </>
  )
}

export default Page
